import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCrown } from 'react-icons/fa';
import Header from './Header';
import Navigation from './Navigation';
import './scss/myProfile.scss';
import Awards from './components/Awards';
import { AwardTitle } from './data/achievements';
import { checkAuthAndRedirect } from './utils/auth';
import PlayerRatings from './components/PlayerRatings';
import PlayerShirt from './components/match/PlayerShirt';
import { getColouredShirtColour, isWinsDrawsLossesHidden, isPlayerRatingsHidden } from './utils/teamSettings';
import ProFeatureModal from './components/ProFeatureModal';
import MatchReportMentions from './components/MatchReportMentions';
import api from './services/apiService';
import { authService } from './services/authService';

function MyProfile() {
  const navigate = useNavigate();
  const { playerId } = useParams();
  const sessionPlayerId = sessionStorage.getItem('player_id');
  const profileId = playerId || sessionPlayerId;
  const [totalGames, setTotalGames] = useState(0);
  const [totalGoals, setTotalGoals] = useState(0);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [draws, setDraws] = useState(0);
  const [matchResults, setMatchResults] = useState([]);
  const [motmCount, setMotmCount] = useState(0);
  const [potmCount, setPotmCount] = useState(0);
  const [player, setPlayer] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [playerRatings, setPlayerRatings] = useState({
    pace: 90,
    shooting: 89,
    passing: 82,
    dribbling: 93,
    defending: 35,
    physical: 78
  });
  const isManager = authService.isManager();
  const isParent = authService.isParent();
  const hideWinsDrawsLosses = isWinsDrawsLossesHidden();
  const hidePlayerRatings = isPlayerRatingsHidden();
  const [showDummyData, setShowDummyData] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);

  // Calculate Pro status from API response data
  const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
  const isPro = isProFromAPI;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSeasonDropdown && !event.target.closest('.season-selector-wrapper')) {
        setShowSeasonDropdown(false);
      }
    };

    if (showSeasonDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSeasonDropdown]);

  // Define fetch functions using useCallback so they can be used in multiple useEffects
  const fetchProfileStats = useCallback(async () => {
    try {
      // Set dummy stats for profile 629 (demo profile)
      if (profileId === '629') {
        setTotalGames(15);
        setTotalGoals(8);
        setMotmCount(3);
        setPotmCount(2);
        return;
      }
      
      const teamId = sessionStorage.getItem('selected_team_id');
      let url = `${process.env.REACT_APP_API_URL}/get_profile_stats.php?player_id=${profileId}&team_id=${teamId}`;
      
      // Add season_id if selected
      if (selectedSeason?.season_id) {
        url += `&season_id=${selectedSeason.season_id}`;
      }
      
      const response = await api.get(url);
      const result = response.data;
      if (result.success) {
        setTotalGames(result.totalGames);
        setTotalGoals(result.totalGoals);
        if (result.motmCount !== undefined) {
          setMotmCount(result.motmCount);
        }
        setPotmCount(result.potmCount || 0);
      } else {
        console.error('Failed to fetch profile stats');
      }
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  }, [profileId, selectedSeason]);

  const fetchWinsLosses = useCallback(async () => {
    try {
      // Set dummy wins/losses for profile 629 (demo profile)
      if (profileId === '629') {
        setWins(10);
        setLosses(3);
        setDraws(2);
        return;
      }
      
      const teamId = sessionStorage.getItem('selected_team_id');
      let url = `${process.env.REACT_APP_API_URL}/get_profile_wins_losses.php?player_id=${profileId}&team_id=${teamId}`;
      
      // Add season_id if selected
      if (selectedSeason?.season_id) {
        url += `&season_id=${selectedSeason.season_id}`;
      }
      
      const response = await api.get(url);
      const result = response.data;
      if (result.success) {
        setWins(result.wins);
        setLosses(result.losses);
        setDraws(result.draws);
      }
    } catch (error) {
      console.error('Error fetching wins/losses:', error);
    }
  }, [profileId, selectedSeason]);

  const fetchMatchResults = useCallback(async () => {
    try {
      // Set dummy match results for profile 629 (demo profile)
      if (profileId === '629') {
        const dummyMatches = [
          {
            match_id: '1',
            date: '2025-07-30',
            team_a: 'Whites',
            team_b: 'Colours',
            score_white: 3,
            score_colour: 2,
            colour: 'White',
            goalScored: '2'
          },
          {
            match_id: '2',
            date: '2025-07-23',
            team_a: 'Whites',
            team_b: 'Colours',
            score_white: 1,
            score_colour: 1,
            colour: 'White',
            goalScored: '1'
          },
          {
            match_id: '3',
            date: '2025-07-16',
            team_a: 'Whites',
            team_b: 'Colours',
            score_white: 2,
            score_colour: 0,
            colour: 'White',
            goalScored: '1'
          }
        ];
        setMatchResults(dummyMatches);
        setShowDummyData(true);
        return;
      }
      
      const teamId = sessionStorage.getItem('selected_team_id');
      const token = sessionStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_URL}/get_match_results.php?player_id=${profileId}&team_id=${teamId}`;
      
      // Add season_id if selected
      if (selectedSeason?.season_id) {
        url += `&season_id=${selectedSeason.season_id}`;
      }
      
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = response.data;
      if (result.success) {
        const realMatches = result.matches.filter(match => {
          return match.match_id && 
                 match.match_id !== '1' && 
                 match.date && 
                 match.score_white !== null && 
                 match.score_colour !== null &&
                 !match.isDummy;
        });
        
        setMatchResults(realMatches);
        
        if (realMatches && realMatches.length > 0) {
          console.log('Real matches found:', realMatches.length);
          setShowDummyData(false);
        } else {
          console.log('No real matches found, showing dummy data if applicable');
        }
      } else {
        console.error('Failed to fetch match results');
      }
    } catch (error) {
      console.error('Error fetching match results:', error);
    }
  }, [profileId, selectedSeason]);

  useEffect(() => {
    checkAuthAndRedirect(navigate);
    
    // Show dummy data for profile 629 (demo profile)
    if (profileId === '629') {
      setShowDummyData(true);
    }
    
    const fetchSubscriptionStatus = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const teamId = sessionStorage.getItem('selected_team_id');
        const url = teamId 
          ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
          : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
        
        const response = await api.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          setSubscriptionStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    const fetchPlayerProfile = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const url = teamId 
          ? `${process.env.REACT_APP_API_URL}/get_player_profile.php?player_id=${profileId}&team_id=${teamId}`
          : `${process.env.REACT_APP_API_URL}/get_player_profile.php?player_id=${profileId}`;
        
        const response = await api.get(url);
        const result = response.data;
        
        if (result.success) {
          setPlayer(result.player);
          if (result.player) {
            setPlayerRatings({
              pace: result.player.pace || 0,
              shooting: result.player.shooting || 0,
              passing: result.player.passing || 0,
              dribbling: result.player.dribbling || 0,
              defending: result.player.defending || 0,
              physical: result.player.physical || 0
            });
          }
        }
      } catch (error) {
        console.error('Error fetching player profile:', error);
      }
    };

    const fetchSeasons = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        if (!teamId) return;
        
        const token = sessionStorage.getItem('token');
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/admin/get_seasons.php?team_id=${teamId}&include_active=true`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const result = response.data;
        
        if (result.success) {
          setSeasons(result.seasons || []);
          // Set current season as default
          if (result.current_season) {
            setSelectedSeason(result.current_season);
          } else if (result.seasons && result.seasons.length > 0) {
            // Find active season or use most recent
            const activeSeason = result.seasons.find(s => s.is_active === 1);
            setSelectedSeason(activeSeason || result.seasons[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
      }
    };

    fetchSubscriptionStatus();
    fetchPlayerProfile();
    fetchSeasons();
  }, [navigate, profileId, isParent]);

  // Fetch stats when season changes or on initial load
  useEffect(() => {
    if (selectedSeason !== null) {
      fetchProfileStats();
      fetchWinsLosses();
      fetchMatchResults();
    }
  }, [selectedSeason, profileId, fetchProfileStats, fetchWinsLosses, fetchMatchResults]);

  useEffect(() => {
    console.log('Stats check - Games:', totalGames, 'Goals:', totalGoals, 'MOTM:', motmCount, 'Matches:', matchResults?.length);
    
    // If we have any real stats, don't show dummy data
    if (totalGames > 0 || totalGoals > 0 || motmCount > 0 || (matchResults && matchResults.length > 0)) {
      console.log('Real stats found, hiding dummy data');
      setShowDummyData(false);
    }
    
    // Additional check: if we have real matches, ensure dummy data is hidden
    if (matchResults && matchResults.length > 0) {
      const hasRealMatches = matchResults.some(match => 
        match.match_id && 
        match.match_id !== '1' && 
        match.date && 
        match.score_white !== null && 
        match.score_colour !== null &&
        !match.isDummy
      );
      
      if (hasRealMatches) {
        console.log('Real matches detected, ensuring dummy data is hidden');
        setShowDummyData(false);
      }
    }
  }, [totalGames, totalGoals, motmCount, matchResults]);

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat('en-GB', options).format(new Date(dateString));
  };

  const handleProOverlayClick = (e) => {
    console.log('Pro overlay clicked!', e);
    e.preventDefault();
    e.stopPropagation();
    setShowProModal(true);
  };

  return (
    <div className="MyProfile container">
      <Header />
      <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      <div className="player-header-section">
        <h1>
          {playerId ? `${player?.first_name} ${player?.last_name}` : 'My Profile'}
        </h1>
        <div className="season-selector-wrapper">
          {seasons.length > 0 && (
            <div className="season-selector" onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}>
              <span>{selectedSeason?.name || 'Select Season'}</span>
              <span className="dropdown-arrow">{showSeasonDropdown ? '▲' : '▼'}</span>
              {showSeasonDropdown && (
                <div className="season-dropdown">
                  {seasons.map(season => (
                    <div 
                      key={season.season_id}
                      className={`season-option ${selectedSeason?.season_id === season.season_id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSeason(season);
                        setShowSeasonDropdown(false);
                      }}
                    >
                      {season.name}
                      {season.is_active === 1 && <span className="active-badge">Current</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {showDummyData && (
        <div className="dummy-data-notice global-notice">
          <h4>Welcome to FirstWhistle!</h4>
          <p>We're showing example data to help you experience the app. 
          Your profile will be populated with real stats as you play matches.</p>
        </div>
      )}

      <div className={`player-intro-section${hidePlayerRatings ? ' ratings-hidden' : ''}`}>
        <PlayerShirt 
          lastName={player?.last_name}
          shirtNumber={player?.shirt_number}
          color={getColouredShirtColour()}
        />
        {!hidePlayerRatings && (
          <div className="player-text-wrapper">
            <div className="player-text">
              <PlayerRatings ratings={playerRatings} />
            </div>
            {!isPro && !isManager && (
              <>
                <div className="blurred-overlay"></div>
                <div className="pro-overlay" onClick={handleProOverlayClick}>
                  <span>Upgrade to Pro to View Player Ratings</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      <div className="awards-container">
        <h2 className="section-title">
          {playerId ? `${player?.first_name}'s Achievements` : 'My Achievements'}
        </h2>
        <Awards 
          showDummy={isParent && showDummyData || profileId === '629'} 
          showHeader={false}
          seasonId={selectedSeason?.season_id || null}
        />
      </div>

      <h2 className="section-title">{playerId ? `${player?.first_name}'s Stats` : 'My Stats'}</h2>
      <div className="stats-container-wrapper">
        <div className="stats-container">
          <div className="stats-column">
            <h3><AwardTitle title="Parents Player of the Match" /></h3>
            <p className="audiowide">{motmCount || 0}</p>
          </div>
          <div className="stats-column">
            <h3><AwardTitle title="Managers Player of the Match" /></h3>
            <p className="audiowide">{potmCount || 0}</p>
          </div>
        </div>
        <div className="stats-container">
          <div className={`stats-column ${!isPro && !isManager ? 'pro-locked' : ''}`}>
            <h3>Appearances</h3>
            <p className="audiowide">{totalGames || 0}</p>
          </div>
        <div className={`stats-column ${!isPro && !isManager ? 'pro-locked' : ''}`}>
            <h3>Goals</h3>
            <p className="audiowide">{totalGoals || 0}</p>
          </div>
        </div>
        {!hideWinsDrawsLosses && (
        <div className="stats-container">
          <div className={`stats-column ${!isPro && !isManager ? 'pro-locked' : ''}`}>
            <h3>Wins</h3>
            <p className="audiowide">{wins || 0}</p>
          </div>
          <div className={`stats-column ${!isPro && !isManager ? 'pro-locked' : ''}`}>
            <h3>Draws</h3>
            <p className="audiowide">{draws || 0}</p>
          </div>
          <div className={`stats-column ${!isPro && !isManager ? 'pro-locked' : ''}`}>
            <h3>Losses</h3>
            <p className="audiowide">{losses || 0}</p>
          </div>
        </div>
        )}
      </div>
      {matchResults && matchResults.length > 0 && !showDummyData && (
        <div className="match-results">
          <h3 className="section-title">Last 5 Matches</h3>
          <div className="last-results-wrapper">
            <div className="last-results">
              <ul className="results-list">
                {matchResults.map((match, index) => (
                  <li 
                    key={index} 
                    className={`result-item ${!isPro && !isManager && index > 0 ? 'blurred' : ''}`} 
                    onClick={() => navigate(`/match/${match.match_id}`)}
                  >
                    <div className="result-info">
                      <span className="date">
                        {new Date(match.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'numeric',
                          year: '2-digit'
                        })}
                      </span>
                      <span className={`team-a ${match.colour === 'White' ? 'player-team' : ''}`}>
                        {match.team_a || 'Whites'}
                      </span>
                      <span className="score">
                        {match.score_white} - {match.score_colour}
                      </span>
                      <span className={`team-b ${match.colour === 'Colour' ? 'player-team' : ''}`}>
                        {match.team_b || 'Colours'}
                      </span>
                      <span className="goals">
                        {parseInt(match.goalScored) > 0 ? `⚽ ${match.goalScored}` : '-'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      <div className="match-report-mentions-section">
        {console.log('ProfileId being passed to MatchReportMentions:', profileId)}
        <MatchReportMentions 
          playerId={profileId} 
          isPro={isPro} 
          isManager={isManager} 
        />
      </div>
      <ProFeatureModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        featureName="Player Profile"
      />
    </div>
  );
}

export default MyProfile;